import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { WorkOrderStatus } from '../../enums/workOrderStatus';
import type { WorkOrderListItem } from '../../types/InfrastructureManagerTypes';
import type { WorkOrderListItem as ApiWorkOrderListItem } from '../../models/assetWorkOrder.model';
import useWorkOrderService from '../../services/WorkOrderService/workOrder.service';

const NO_WORK_ORDERS: WorkOrderListItem[] = [];

const API_DATE_FORMAT = 'YYYY-MM-DD';

const toListItem = (item: ApiWorkOrderListItem): WorkOrderListItem => ({
  id: item.id,
  number: item.number,
  title: item.title ?? '',
  status: item.status as WorkOrderStatus,
  scheduleDates: item.scheduleDates ?? [],
  completedAt: item.completedAt ?? undefined,
  dateAssigned: item.createdAt ?? '',
  updatedAt: item.updatedAt ?? item.createdAt ?? '',
  assets: (item.assets ?? []).map(({ layerId, keyValue, displayValue }) => ({
    layerId: layerId ?? 0,
    keyValue: keyValue ?? '',
    displayValue: displayValue ?? keyValue ?? '',
  })),
  hasDeletedAsset: item.hasDeletedAsset ?? false,
  contacts: (item.contacts ?? []).map(({ id, name }) => ({ id, name: name ?? '' })),
  createdBy: item.createdBy ? { id: item.createdBy.id, name: item.createdBy.name ?? '' } : null,
});

export type StatusDates = Partial<Pick<WorkOrderListItem, 'scheduleDates' | 'completedAt'>>;

interface WorkOrderDataContextValue {
  orders: WorkOrderListItem[];
  isLoading: boolean;
  updateStatus: (id: number, to: WorkOrderStatus, dates?: StatusDates) => void;
}

const WorkOrderDataContext = createContext<WorkOrderDataContextValue | null>(null);

const WorkOrderDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { getAllWorkOrders, updateWorkOrderStatus } = useWorkOrderService();
  // The board shows every work order, so it reads all pages rather than one.
  const { data, isLoading } = useQuery(getAllWorkOrders());
  const { mutate } = useMutation(updateWorkOrderStatus());

  const orders = useMemo(() => data?.workOrders?.map(toListItem) ?? NO_WORK_ORDERS, [data]);

  // Dates go with the status that needs them (schedule for Scheduled, the day for Completed), as API dates.
  const updateStatus = useCallback(
    (id: number, to: WorkOrderStatus, dates?: StatusDates) =>
      mutate({
        id,
        status: to,
        // A FROM/TO pair on the same day is one date.
        scheduleDates: dates?.scheduleDates && [...new Set(dates.scheduleDates.filter(Boolean).map((date) => dayjs(date).format(API_DATE_FORMAT)))],
        completedAt: dates?.completedAt ? dayjs(dates.completedAt).format(API_DATE_FORMAT) : undefined,
      }),
    [mutate],
  );

  const value = useMemo(() => ({ orders, isLoading, updateStatus }), [orders, isLoading, updateStatus]);

  return <WorkOrderDataContext.Provider value={value}>{children}</WorkOrderDataContext.Provider>;
};

const useWorkOrderData = () => {
  const context = useContext(WorkOrderDataContext);
  if (!context) throw new Error('useWorkOrderData must be used within WorkOrdersProvider');
  return context;
};

export { WorkOrderDataProvider, useWorkOrderData };
